import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { Box, Grid } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { CreateSfWalletRequestDto, CryptoAddressProtocol, ISfWallet } from '@sparling/financial-common'
import { useDispatch, useSelector } from 'react-redux'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { InputSelect } from '../../../../components/InputSelect'
import { createEnum } from '../../../../utils/createEnum'
import { InputField } from '../../../../components/InputField'
import { Modal } from '../../../../components/Modal'
import { createSfWalletRequest, updateSfWalletRequest } from '../../../../store/actions'
import { availableCryptoCurrenciesEnumSelector } from '../../../../store/selectors'

interface Props {
  isOpen: boolean
  modalClose: () => void
  fields: ISfWallet
}

export const WalletsModal: FC<Props> = ({ isOpen, fields, modalClose }) => {
  const dispatch = useDispatch()
  const availableCryptoCurrencies = useSelector(availableCryptoCurrenciesEnumSelector)

  const formMethods = useForm<ISfWallet>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(CreateSfWalletRequestDto),
    defaultValues: useMemo(() => fields || {}, [fields]),
  })

  const {
    handleSubmit,
    formState: { isValid },
    setValue,
    reset,
  } = formMethods

  useEffect(() => {
    if (fields && Object.keys(fields).length > 0) {
      Object.entries(fields).forEach(([key, value]) => {
        setValue(key as keyof ISfWallet, value)
      })
    }
  }, [fields, setValue])

  const formFields = [
    { type: 'select', label: 'Currency', name: 'currency', enums: availableCryptoCurrencies },
    { type: 'text', label: 'Address', name: 'address' },
    { type: 'text', label: 'Address Suffix', name: 'addressSuffix' },
    { type: 'select', label: 'Address Protocol', name: 'addressProtocol', enums: createEnum(CryptoAddressProtocol) },
    {
      type: 'text',
      label: 'Description',
      name: 'description',
      multiline: true,
      rows: 4,
      fullWidth: true,
      withoutPadding: true,
    },
  ]

  const formSubmitHandle = useCallback(
    (data: ISfWallet) => {
      dispatch('id' in data ? updateSfWalletRequest(data) : createSfWalletRequest(data))
      reset()
      modalClose?.()
    },
    [dispatch, modalClose, reset],
  )

  return (
    <Modal
      title={fields ? 'Edit Wallet' : 'Add Wallet'}
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(formSubmitHandle)}
      submitTitle={'Submit'}
      cancelTitle={'Cancel'}
      isSubmitDisabled={!isValid}
      width={680}
      submitButtonProps={{
        type: 'button',
      }}
    >
      <FormProvider {...formMethods}>
        <Grid item xs={12} sm={12} container direction="column">
          {formFields.map(({ type, label, name, ...props }) =>
            type === 'select' ? (
              <InputSelect
                key={name}
                labelName={label}
                inputName={name}
                placeholder="Please Select"
                fullWidth
                mb={20}
                {...props}
              />
            ) : (
              <InputField key={name} labelName={label} inputName={name} inputType="text" fullWidth {...props} />
            ),
          )}
          <Box mb={4} />
        </Grid>
      </FormProvider>
    </Modal>
  )
}
